import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { Button } from "../../components/common/Button";
import { Select } from "../../components/common/Select";
import { Spinner } from "../../components/common/Spinner";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { getChannelTitles, getSmartEvents, patchSmartEvents } from "../../api/config";
import type { Device } from "../../types";

const SENSITIVITY_OPTS = [
  { value: "Low", label: "Baja" },
  { value: "Middle", label: "Media" },
  { value: "High", label: "Alta" },
];

interface SmdEntry {
  Enable?: boolean;
  Sensitivity?: string;
  ObjectTypes?: { Human?: boolean; Vehicle?: boolean; Animal?: boolean };
}

function flattenForSet(prefix: string, value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (p: string, v: unknown): void => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) { v.forEach((x, i) => walk(`${p}[${i}]`, x)); return; }
    if (typeof v === "object") { Object.entries(v as Record<string, unknown>).forEach(([k, vv]) => walk(p ? `${p}.${k}` : k, vv)); return; }
    if (typeof v === "boolean") { out[p] = v ? "true" : "false"; return; }
    out[p] = String(v);
  };
  walk(prefix, value);
  return out;
}

export function SmartEventsTab({ deviceId, device }: { deviceId: number; device: Device }) {
  const [data, setData] = useState<{
    smd: { SmartMotionDetect?: SmdEntry[] } & Record<string, any>;
    ivs_supported: boolean;
    ivs: Record<string, any>;
  } | null>(null);
  const [channelIdx, setChannelIdx] = useState(0);
  const [form, setForm] = useState<SmdEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channelNames, setChannelNames] = useState<string[]>([]);
  const [applyAllOpen, setApplyAllOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await getSmartEvents(deviceId);
      setData(d);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo eventos IA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    getChannelTitles(deviceId)
      .then((d) => setChannelNames(d.titles.map((t) => t.name)))
      .catch(() => setChannelNames([]));
  }, [deviceId]);

  useEffect(() => {
    const arr = data?.smd?.SmartMotionDetect;
    const ch1 = channelIdx + 1;
    let entry: SmdEntry;
    if (Array.isArray(arr) && arr[ch1] && Object.keys(arr[ch1]).length > 0) {
      entry = arr[ch1] as SmdEntry;
    } else {
      entry = { Enable: false, Sensitivity: "Middle", ObjectTypes: { Human: true, Vehicle: false, Animal: false } };
    }
    setForm(JSON.parse(JSON.stringify(entry)));
  }, [data, channelIdx]);

  const totalChannels = device.channels_count || 1;
  const channelOptions = useMemo(
    () => Array.from({ length: totalChannels }, (_, i) => ({
      value: String(i),
      label: channelNames[i] ? `CH${i + 1} - ${channelNames[i]}` : `CH${i + 1}`,
    })),
    [totalChannels, channelNames],
  );

  if (loading || !data) return <Spinner />;

  const ivsSupported = data.ivs_supported;
  const smdHasError = "error" in (data.smd || {});

  const updateForm = (patch: Partial<SmdEntry>) => setForm((p) => p ? { ...p, ...patch } : p);
  const updateObjectTypes = (k: "Human" | "Vehicle" | "Animal", v: boolean) =>
    setForm((p) => p ? { ...p, ObjectTypes: { ...(p.ObjectTypes || {}), [k]: v } } : p);

  const buildPayload = (idx: number): Record<string, string> => {
    if (!form) return {};
    const ch1 = idx + 1;
    return flattenForSet(`SmartMotionDetect[${ch1}]`, form);
  };

  const save = async () => {
    setSaving(true);
    try {
      await patchSmartEvents(deviceId, buildPayload(channelIdx));
      toast.success(`SMD CH${channelIdx + 1} actualizado`);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo al guardar");
    } finally {
      setSaving(false);
    }
  };

  const applyToAll = async () => {
    setApplyAllOpen(false);
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (let i = 0; i < totalChannels; i++) {
        Object.assign(payload, buildPayload(i));
      }
      await patchSmartEvents(deviceId, payload);
      toast.success(`Aplicado a ${totalChannels} canales`);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo aplicando a todos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Eventos inteligentes (SMD / IVS)</h2>
          <p className="text-xs text-muted mt-0.5">
            Detección por IA del NVR: humanos, vehículos, animales.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Select
            label="Canal"
            options={channelOptions}
            value={String(channelIdx)}
            onChange={(e) => setChannelIdx(Number(e.target.value))}
            className="min-w-[200px]"
          />
          <Button variant="secondary" onClick={() => setApplyAllOpen(true)} disabled={saving || !form}>
            Aplicar a todos los canales
          </Button>
        </div>
      </header>

      <section className={clsx("border border-line rounded p-3 bg-panel/30 space-y-3", smdHasError && "opacity-60")}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">SMD — Smart Motion Detection</h3>
          <span className={clsx("px-2 py-0.5 rounded border text-xs",
            !form?.Enable ? "bg-line text-muted border-line" : "bg-green-900/40 text-green-300 border-green-700",
          )}>
            {form?.Enable ? "Activado" : "Desactivado"}
          </span>
        </div>
        {smdHasError && (
          <div className="text-xs text-yellow-300">El DVR no expone SmartMotionDetect en este canal.</div>
        )}
        {form && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.Enable}
                onChange={(e) => updateForm({ Enable: e.target.checked })}
              />
              <span>Habilitar SMD en este canal</span>
            </label>
            <div className={clsx("grid grid-cols-2 md:grid-cols-4 gap-3", !form.Enable && "opacity-60 pointer-events-none")}>
              <Select
                label="Sensibilidad"
                options={SENSITIVITY_OPTS}
                value={String(form.Sensitivity || "Middle")}
                onChange={(e) => updateForm({ Sensitivity: e.target.value })}
              />
              <label className="flex items-center gap-2 text-xs mt-5">
                <input type="checkbox" checked={!!form.ObjectTypes?.Human} onChange={(e) => updateObjectTypes("Human", e.target.checked)} />
                <span>Detectar personas</span>
              </label>
              <label className="flex items-center gap-2 text-xs mt-5">
                <input type="checkbox" checked={!!form.ObjectTypes?.Vehicle} onChange={(e) => updateObjectTypes("Vehicle", e.target.checked)} />
                <span>Detectar vehículos</span>
              </label>
              <label className="flex items-center gap-2 text-xs mt-5">
                <input type="checkbox" checked={!!form.ObjectTypes?.Animal} onChange={(e) => updateObjectTypes("Animal", e.target.checked)} />
                <span>Detectar animales</span>
              </label>
            </div>
          </>
        )}
      </section>

      <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">IVS — Análisis de video inteligente</h3>
          <span className={clsx("px-2 py-0.5 rounded border text-xs",
            ivsSupported ? "bg-green-900/40 text-green-300 border-green-700" : "bg-line text-muted border-line",
          )}>
            {ivsSupported ? "Soportado" : "No disponible"}
          </span>
        </div>
        {ivsSupported ? (
          <p className="text-xs text-muted">
            Las reglas IVS (tripwire, intrusion, abandoned object, etc.) se configuran desde la cámara IPC
            o desde el menú avanzado del NVR. Esta sección muestra el estado actual.
          </p>
        ) : (
          <p className="text-xs text-muted">
            Este modelo de NVR no expone reglas IVS via HTTP API. Se administra desde la cámara IPC directamente.
            En este NVR la detección IA está cubierta por SMD (arriba).
          </p>
        )}
      </section>

      <div className="flex gap-2">
        <Button variant="primary" onClick={save} loading={saving} disabled={!form || smdHasError}>Guardar SMD</Button>
        <Button variant="ghost" onClick={reload} disabled={saving}>Restablecer</Button>
      </div>

      <ConfirmDialog
        open={applyAllOpen}
        title="Aplicar SMD a todos los canales"
        message={`Esta accion va a sobrescribir la configuracion de Smart Motion Detect en los ${totalChannels} canales con la del canal actual. ¿Continuar?`}
        confirmText="Aplicar a todos"
        destructive
        onConfirm={applyToAll}
        onCancel={() => setApplyAllOpen(false)}
      />
    </div>
  );
}
